import { create } from 'zustand';

/**
 * Live snapshot of thread-bound (sticky) attachment totals.
 *
 * Server-side every upload bound to the active thread is auto-included in
 * each chat turn until the user explicitly deletes it. We need a way for
 * the upload adapter (which lives outside the React tree, captured by
 * `useChatRuntimeWithTransport`) to validate caps that depend on those
 * sticky bytes BEFORE issuing a new POST /uploads.
 *
 * The sticky strip component (`<StickyThreadAttachments />`) owns the
 * TanStack Query that fetches `GET /uploads?threadId=<id>`. On every
 * successful refetch it writes the totals here via `setTotals`. The
 * upload adapter then reads via `getState()` (synchronous, inside its
 * `add()` async generator) to compute the projected request size.
 *
 * Mirror of the thread-list-store shape (`Zustand` + `version: number`
 * counter); this one is a typed `{bytes, imageCount}` slot instead of a
 * version counter because the consumer needs the actual values, not just
 * a refresh signal.
 */
interface StickyAttachmentsState {
  bytes: number;
  imageCount: number;
  setTotals: (next: { bytes: number; imageCount: number }) => void;
  reset: () => void;
}

export const useStickyAttachmentsStore = create<StickyAttachmentsState>((set) => ({
  bytes: 0,
  imageCount: 0,
  setTotals: ({ bytes, imageCount }) => set({ bytes, imageCount }),
  reset: () => set({ bytes: 0, imageCount: 0 }),
}));
